// crm-client.js — thin, DOM-free CRM transport (Brief 188).
//
// Talks to the CRM's web contract (Swagger UI at `${CRM_HOST}/api-docs/`).
// Rule #1 hygiene: no DOM, no imports from src/screens/ or src/ui/. The only
// project imports are auth-store.js (bearer token) and config/crm-host.js
// (build-time host). Session-invalidation surfaces via the onSessionInvalid()
// callback seam below — this module never touches the router or the DOM.
//
// Every exported function resolves a CrmResult; NONE of them throw for a
// CRM-level failure (bad credentials, a network error, an unexpected HTTP
// status) — callers branch on `result.success`. The one exception is
// crmStartTrial(), which throws synchronously because the endpoint isn't
// wired yet (see its docblock) — that's a programmer-error guard, not a
// CRM-level outcome.
//
// Web callers authenticate with `Authorization: Bearer` and must NEVER send
// `uaid` or `device_time` (every web endpoint states this) — performRequest()
// below is the one place a body is built, and neither field is ever added
// there.
//
// BRIEF 194 — TOKEN LIFETIME. The web-login JWT lasts 7200s. Two layers keep a
// learner signed in past that without ever storing the password:
//   • proactive: the sync engine asks `refreshSessionIfDue()` ahead of every
//     touch-base, so the token is renewed ~5 min before it would expire;
//   • reactive: a 401 + error code 20000 that slips through is refreshed and
//     the original request retried ONCE, transparently to the caller.
// The refresh presents the Bearer and NOTHING else: there is no refresh token
// on the client at all — the server holds it against the session (Allen,
// 2026-11-01). A single-flight slot still guarantees at most one refresh at a
// time, because the JWT itself rotates ("a copied token stops working once the
// real client refreshes") and two concurrent renewals would race over which
// token the store ends up holding. 409 + 91049 (displaced) is never refreshed
// and never retried — see SessionInvalidReason below.

import { CRM_HOST } from "../config/crm-host.js";
import {
  getSession,
  applyRefreshedSession,
  isRefreshDue,
  DEV_BYPASS_TOKEN,
} from "./auth-store.js";

// Swagger displays paths as `/app/...`; the OpenAPI basePath is almost
// certainly `/api/v1` (unconfirmed — see Brief 188 §"Pre-flight status").
// If the live spec's servers/basePath block differs, change only this
// constant.
const CRM_BASE = `${CRM_HOST}/api/v1`;

/**
 * Normalised result every exported function resolves to. Never throws for a
 * CRM-level failure — network/parse failures also resolve (never reject) as
 * { success:false, error }. Callers branch on `success`.
 * @typedef {{ success: boolean, data?: any, error?: CrmError }} CrmResult
 * @typedef {{ code: string|number, message: string, kind: CrmErrorKind, data?: any, retryAfter?: string | null }} CrmError
 * @typedef {"bad_credentials"|"session_expired"|"session_displaced"|"progress_conflict"|"network"|"generic"|"validation_error"|"account_locked"|"rate_limited"} CrmErrorKind
 */

/**
 * Why a session became unusable. The two cases are NOT interchangeable
 * (Brief 194):
 *
 *   • `"expired"` — the JWT aged out (401 + error code 20000) AND the refresh
 *     below could not renew it. Nothing is wrong with the learner's account;
 *     they simply need to sign in again. The app shell is allowed to let the
 *     current item finish and save before it routes to /login.
 *   • `"displaced"` — another device took this session over (409 + error code
 *     91049). The server has already handed the session to someone else, so
 *     there is nothing to finish: sign out at once (Brief 025 §10).
 *
 * @typedef {"expired"|"displaced"} SessionInvalidReason
 */

/** @type {Array<(reason: SessionInvalidReason) => void>} */
let sessionInvalidListeners = [];

/**
 * Register a callback invoked whenever a session is finally unusable —
 * `kind:"session_expired"` whose refresh failed, or `kind:"session_displaced"`
 * (409/91049). Callers (route guard, settings screen, sync engine) use this to
 * clear the session and route to /login — crm-client itself never touches the
 * DOM or the router.
 *
 * NOTE a 401 alone no longer fires this: Brief 194 refreshes and retries first,
 * and only a FAILED refresh reaches the listeners. The callback receives the
 * reason so the two cases can diverge in the UI.
 *
 * @param {(reason: SessionInvalidReason) => void} cb
 * @returns {() => void} unsubscribe
 */
export function onSessionInvalid(cb) {
  sessionInvalidListeners.push(cb);
  return () => {
    sessionInvalidListeners = sessionInvalidListeners.filter((c) => c !== cb);
  };
}

/** @param {SessionInvalidReason} reason */
function notifySessionInvalid(reason) {
  for (const cb of sessionInvalidListeners) {
    try { cb(reason); } catch (e) { console.error("[crm-client] onSessionInvalid listener threw", e); }
  }
}

/**
 * The ONLY place password encoding happens for web login. Confirmed
 * 2026-07-15 (Chris): `/app/auth/web-login` expects the RAW password over
 * TLS, not MD5 — this differs from iOS's KFELoginManager.m:296 MD5, which is
 * fine, since web-login is a new web-only endpoint. Kept as its own function
 * (rather than inlined) so a future change is one line.
 * @param {string} raw
 * @returns {string}
 */
function preparePassword(raw) {
  return raw;
}

/**
 * @typedef {{
 *   method: string,
 *   path: string,
 *   auth?: boolean,
 *   form?: Record<string, unknown>,
 *   json?: Record<string, unknown>,
 *   query?: Record<string, unknown>,
 *   isLogin?: boolean,
 *   keepalive?: boolean,
 *   noRefresh?: boolean,
 * }} RequestOpts
 */

/**
 * ONE HTTP round-trip. Attaches Authorization: Bearer for authed calls,
 * form-encodes or JSON-encodes the body, and maps transport/HTTP outcomes to
 * a CrmResult. Web NEVER sends uaid or device_time — neither field is ever
 * part of `form`/`json`/`query` in any exported function below.
 *
 * `request()` below wraps this with the Brief 194 refresh-and-retry-once
 * policy; everything that is per-attempt lives here, everything that is
 * per-CALL (the no-session short-circuit, refresh, retry, the
 * session-invalid notification) lives there. Nothing in here notifies — a
 * 401 is a fact about one attempt, not yet a verdict about the session.
 *
 * @param {RequestOpts} opts
 * @returns {Promise<CrmResult>}
 */
async function performRequest(opts) {
  const { method, path, auth = false, form, json, query, isLogin = false, keepalive = false } = opts;

  /**
   * `Accept: application/json` is load-bearing, not politeness (Brief 194
   * follow-up, 2026-11-02). Token expiry is rejected by Laravel Passport's
   * guard BEFORE application logic runs, and Laravel's `Authenticate`
   * middleware only answers an unauthenticated request with JSON when
   * `expectsJson()` is true — which, for the wildcard Accept that `fetch`
   * sends by default, it is NOT. Without this header that layer can answer
   * with a redirect or an
   * HTML body instead of its JSON 401, which is how a real expiry looked
   * nothing like the app-layer 401 we had tested against.
   * @type {Record<string, string>}
   */
  const headers = { Accept: "application/json" };

  if (auth) {
    const token = getSession()?.token;
    // The no-token case never gets here — request() short-circuits it.
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let url = `${CRM_BASE}${path}`;
  if (query) {
    const qs = new URLSearchParams(/** @type {Record<string,string>} */ (query)).toString();
    if (qs) url += `?${qs}`;
  }

  /** @type {string | undefined} */
  let body;
  if (form) {
    headers["Content-Type"] = "application/x-www-form-urlencoded";
    body = new URLSearchParams(/** @type {Record<string,string>} */ (form)).toString();
  } else if (json) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(json);
  }

  let res;
  try {
    // `keepalive` (Brief 025) lets an unload-time flush outlive the page. It is
    // the ONLY unload transport that can carry `Authorization: Bearer` —
    // navigator.sendBeacon cannot set headers at all, and putting a JWT in a
    // query string is not acceptable. Browsers cap a keepalive body at ~64 KB,
    // which is the same ceiling sendBeacon has; sync-engine keeps batches well
    // under it by flushing at every breakpoint.
    /** @type {RequestInit} */
    const init = { method, headers, body };
    if (keepalive) init.keepalive = true;
    // `credentials` is deliberately never set, so the default ("same-origin")
    // applies to every call and the CRM never needs to send
    // Access-Control-Allow-Credentials. Nothing here relies on a cookie —
    // including the refresh, which carries the Bearer and no body.
    res = await fetch(url, init);
  } catch (err) {
    return {
      success: false,
      error: { kind: "network", code: "network", message: err instanceof Error ? err.message : String(err) },
    };
  }

  // The body is READ here but the verdict below does not depend on it.
  //
  // This used to return early on a parse failure, which made the HTTP status
  // unreachable for any response whose body was not the app's JSON — and a 401
  // that landed in that branch came back as `kind:"network"`, so no refresh was
  // ever attempted and nothing told the learner. Allen's 2026-11-02 note is
  // exactly that case: expiry is produced by Passport before application logic,
  // so the body is whatever that layer emits, not the app's `{success,
  // error_code}` envelope. The status is the one thing we can always trust, so
  // it is now mapped first and the body is only ever read for extra detail.
  /** @type {any} */
  let payload = null;
  let bodyParsed = true;
  try {
    payload = await res.json();
  } catch {
    bodyParsed = false;
  }

  // Status code mapping. Contract confirmed with Allen (CRM owner) 2026-08-12:
  //   401 → invalid/missing/expired token (20000)  → session_expired (refreshable)
  //   403 → invalid credentials (20002)            → bad_credentials
  //   409 → 91049 session displaced OR 91048 unpushed-progress conflict
  //   400 → validation error (10002) OR expired license (91012) → validation_error
  //         (differentiated by error_code + server message; login-time
  //         bad-password is 403, NOT 400)
  //   423 → account locked after too many failed logins (20005) → account_locked
  //   429 → rate limited (200/min per IP/account) → rate_limited
  if (res.status === 401) {
    // ANY 401 on an authenticated endpoint means "this token is no longer
    // usable" and is worth exactly one refresh. The STATUS decides that; the
    // body never does. Two different layers of the CRM produce a 401 with
    // different bodies and only one of them carries `error_code: 20000`:
    //   • app layer    — `{success:false, message, errors:[…], error_code:20000}`
    //                    (observed on crm-staging 2026-11-02 for a malformed token)
    //   • Passport     — its own generic shape, no `error_code`, and possibly
    //                    not JSON at all (Allen, 2026-11-02: expiry is rejected
    //                    before application logic runs)
    // Gating the refresh on `error_code === 20000` would therefore miss the
    // real expiry case — the one this whole brief exists for. 20000 is kept
    // only as the reported `code` when the body does not name one.
    //
    // Distinct from 409/91049 (displaced) below, which is NOT refreshable. No
    // notification here — request() decides whether a refresh rescues it.
    return {
      success: false,
      error: { kind: "session_expired", code: payload?.error_code ?? 20000, message: payload?.message ?? "Unauthorized" },
    };
  }

  if (res.status === 403) {
    return { success: false, error: { kind: "bad_credentials", code: payload?.error_code ?? 20002, message: payload?.message ?? "Invalid credentials" } };
  }

  if (res.status === 409) {
    const errorCode = payload?.error_code;
    if (errorCode === 91049) {
      // 91049 = another device took the session over. NOT refreshable and NOT
      // retried: the server has already reassigned the session, so a refresh
      // would be answered with the same 409. Straight to sign-out (Brief 025
      // §10), exactly as before Brief 194.
      notifySessionInvalid("displaced");
      return { success: false, error: { kind: "session_displaced", code: 91049, message: payload?.message ?? "Session displaced" } };
    }
    if (errorCode === 91048) {
      // Unpushed-progress conflict — preserve the full body (options,
      // gap_seconds, previous_device, session_resolution) for the future
      // device-switch UI (Brief 029) to drive its three-option flow.
      return { success: false, error: { kind: "progress_conflict", code: 91048, message: payload?.message ?? "Progress conflict", data: payload } };
    }
    return { success: false, error: { kind: "generic", code: errorCode ?? 409, message: payload?.message ?? "Conflict" } };
  }

  if (res.status === 423) {
    return { success: false, error: { kind: "account_locked", code: payload?.error_code ?? 20005, message: payload?.message ?? "Account locked" } };
  }

  if (res.status === 429) {
    // Some Laravel configs DO return JSON for 429 — handle it here too.
    return { success: false, error: { kind: "rate_limited", code: 429, message: payload?.message ?? "Too many requests, please wait", retryAfter: res.headers.get("Retry-After") } };
  }

  if (res.status === 400) {
    // 400 covers validation errors and expired license — NOT bad credentials
    // (that's 403). Login callers get validation_error (not bad_credentials)
    // so the UX message reflects what actually failed. isLogin retained on
    // the request() opt only so future login-specific 400 semantics have a
    // seam to hang off.
    return {
      success: false,
      error: { kind: "validation_error", code: payload?.error_code ?? 400, message: payload?.message ?? "Bad request" },
    };
  }

  if (!res.ok) {
    return { success: false, error: { kind: "generic", code: payload?.error_code ?? res.status, message: payload?.message ?? `HTTP ${res.status}` } };
  }

  if (!bodyParsed) {
    // A 2xx whose body we cannot read. Every status that carries a verdict has
    // already returned above, so this is the only case left where the body
    // actually mattered.
    return { success: false, error: { kind: "network", code: "parse", message: "Could not parse CRM response" } };
  }

  if (payload?.success === true || payload?.success === 1) {
    return { success: true, data: payload.data ?? payload };
  }

  // 2xx with no explicit success flag — tolerate it as success rather than
  // rejecting a response the spec's basePath uncertainty (Brief 188
  // §"Pre-flight status") might shape slightly differently than expected.
  return { success: true, data: payload };
}

// ---------------------------------------------------------------------------
// 0b. Token refresh (Brief 194) — the refresh-and-retry-once policy, and the
//     single-flight that keeps a rotating JWT survivable.
// ---------------------------------------------------------------------------

/** Allen's refresh endpoint, built 2026-10-31. */
export const REFRESH_PATH = "/app/auth/refresh";

/**
 * What one refresh attempt concluded.
 *   • `"ok"`        — a new token is in the store.
 *   • `"expired"`   — the server refused the refresh credential (401/20000).
 *   • `"displaced"` — the session was taken over meanwhile (409/91049). The
 *                     displaced notification has ALREADY fired.
 *   • `"network"`   — never reached the server (or an unexpected status).
 *   • `"skipped"`   — nothing to refresh: no session, the dev-bypass mock, or
 *                     a refresh already refused for this exact token.
 * @typedef {"ok"|"expired"|"displaced"|"network"|"skipped"} RefreshOutcome
 */

/**
 * The single-flight slot. JWT rotation makes a SECOND concurrent refresh
 * harmful — the two renewals race over which token the store ends up holding,
 * and the loser's token is the one a copied client is supposed to lose — so
 * every caller (proactive scheduler, and each 401 that wants a retry) waits on
 * the same promise. This is a correctness rule, not an optimisation (Brief 194
 * criterion 6).
 * @type {Promise<RefreshOutcome> | null}
 */
let inFlightRefresh = null;

/**
 * The token whose refresh the server REFUSED. Latched so a burst of 401s
 * cannot fire one doomed refresh attempt each; cleared implicitly by a new
 * login, because the comparison is against the token itself.
 * @type {string | null}
 */
let refreshDeniedForToken = null;

/** @returns {boolean} whether a refresh is in flight right now (unload-flush gotcha, tests) */
export function isRefreshInFlight() {
  return inFlightRefresh !== null;
}

/**
 * Test-only reset of the refresh latches. Production never calls this — a real
 * session's latch is keyed on its token and goes away with the next login.
 */
export function __resetRefreshStateForTests() {
  inFlightRefresh = null;
  refreshDeniedForToken = null;
}

/**
 * Normalise the refresh response's field casing, the same way
 * normaliseLoginData does for web-login — Allen (2026-11-01) says the response
 * is the same shape as web-login's credential block, and a live login against
 * crm-staging the same day confirmed it on the wire (docs/194-handover.md
 * §2.1). Tolerant of either casing and of `access_token` naming, as the login
 * normaliser is.
 *
 * The live response also carries `issued_at` / `expires_at`, plus a refresh
 * counter on the session object. All three are deliberately NOT read. The first two
 * are SERVER clock values, while the whole refresh schedule is computed against
 * the CLIENT clock (`issuedAt` is stamped locally and compared only to elapsed
 * time — see auth-store's isRefreshDue). Mixing the two clocks is the skew bug
 * that rule exists to prevent; the only safe use of the server pair would be
 * their difference, as a duration. The counter is server-side diagnosis.
 * @param {any} raw
 * @returns {{ token?: string, tokenType?: string, expiresIn?: number }}
 */
function normaliseRefreshData(raw) {
  if (!raw || typeof raw !== "object") return {};
  return {
    token: raw.token ?? raw.access_token,
    tokenType: raw.token_type ?? raw.tokenType,
    expiresIn: raw.expires_in ?? raw.expiresIn,
  };
}

/**
 * ONE refresh round-trip — raw transport, no single-flight, no store write.
 * `refreshSession()` is what callers normally want; this is exported so the
 * wire shape is testable on its own and so a future caller can drive it
 * directly.
 *
 * `noRefresh: true` is load-bearing: without it a 401 ON THE REFRESH would
 * recurse into another refresh.
 * @returns {Promise<CrmResult>}
 */
export async function crmRefresh() {
  // The current Bearer is the whole request: no body, no cookie, no refresh
  // token (Allen, 2026-11-01 — the server holds the refresh token against the
  // session, and the just-expired JWT is accepted for up to 30 days). `auth:
  // true` attaches it; there is nothing else to send.
  const result = await request({
    method: "POST",
    path: REFRESH_PATH,
    auth: true,
    noRefresh: true,
  });
  if (!result.success) return result;
  return { success: true, data: normaliseRefreshData(result.data) };
}

/**
 * Renew the live token, at most one refresh at a time.
 *
 * On success the new token replaces the old via auth-store, with `issuedAt`
 * re-stamped so the expiry schedule restarts.
 *
 * @returns {Promise<RefreshOutcome>}
 */
export function refreshSession() {
  if (inFlightRefresh) return inFlightRefresh;

  const token = getSession()?.token;
  if (!token) return Promise.resolve(/** @type {RefreshOutcome} */ ("skipped"));
  // The ?dev=1 mock session is not a real session: there is nothing to refresh
  // and refreshing it must not be attempted (Brief 194 gotcha "don't widen the
  // dev-token path").
  if (token === DEV_BYPASS_TOKEN) return Promise.resolve(/** @type {RefreshOutcome} */ ("skipped"));
  if (refreshDeniedForToken === token) {
    // Say so. This state is why the 2026-11-02 live smoke could not be read:
    // the proactive leg had already spent this token's one refresh a beat
    // earlier, so the 401 that actually signed the learner out showed NO
    // refresh call next to it in the Network panel, and nothing distinguished
    // "never tried" from "tried, was refused, and correctly gave up".
    console.warn("[crm-client] refresh already refused for this token — not retrying (session is finished)");
    return Promise.resolve(/** @type {RefreshOutcome} */ ("skipped"));
  }

  const attempt = doRefresh(token).finally(() => {
    if (inFlightRefresh === attempt) inFlightRefresh = null;
  });
  inFlightRefresh = attempt;
  return attempt;
}

/**
 * @param {string} tokenAtStart the token this attempt is spending
 * @returns {Promise<RefreshOutcome>}
 */
async function doRefresh(tokenAtStart) {
  console.info(`[crm-client] refreshing the session token via POST ${REFRESH_PATH}`);
  const res = await crmRefresh();

  if (res.success && res.data?.token) {
    const written = applyRefreshedSession(res.data);
    console.info(`[crm-client] refresh ${written ? "succeeded — new token stored" : "returned a token but the store refused it"}`);
    return written ? "ok" : "skipped";
  }

  const kind = res.error?.kind;
  console.warn(`[crm-client] refresh REFUSED (${kind ?? "unknown"}: ${res.error?.message ?? "no message"}) — the session is finished`);
  if (kind === "session_displaced") {
    // performRequest already fired notifySessionInvalid("displaced").
    refreshDeniedForToken = tokenAtStart;
    return "displaced";
  }
  if (kind === "network") {
    // Transient: do NOT latch, so a later attempt may still succeed.
    return "network";
  }
  // 401/20000 on the refresh itself, or any other refusal (403, 400, a 2xx
  // with no token). Terminal for this token.
  refreshDeniedForToken = tokenAtStart;
  return kind === "session_expired" ? "expired" : "network";
}

/**
 * Proactive leg (Brief 194 criterion 1): refresh only when the live token is
 * within `marginMs` of its nominal expiry. Called from the sync engine's
 * touch-base, so it gets a look-in on every heartbeat and before every flush.
 * Returns `"skipped"` when nothing was due.
 * @param {{ now?: number, marginMs?: number }} [opts]
 * @returns {Promise<RefreshOutcome>}
 */
export function refreshSessionIfDue(opts = {}) {
  if (!isRefreshDue(opts)) return Promise.resolve(/** @type {RefreshOutcome} */ ("skipped"));
  return refreshSession();
}

/**
 * The per-CALL chokepoint, wrapping performRequest with the Brief 194 policy:
 *
 *   1. No token at all → resolve without a network call.
 *   2. 401/20000 → refresh ONCE, retry the original request ONCE. Transparent
 *      to the caller, which sees only the retry's result.
 *   3. Refresh failed → the session is finally invalid: notify "expired".
 *   4. 409/91049 → already notified "displaced" inside performRequest; never
 *      refreshed, never retried.
 *
 * @param {RequestOpts} opts
 * @returns {Promise<CrmResult>}
 */
async function request(opts) {
  const { auth = false, keepalive = false, noRefresh = false } = opts;

  const tokenAtSend = auth ? getSession()?.token : undefined;
  if (auth && !tokenAtSend) {
    // No session to attach — resolve without a network call (Brief 188 §0).
    return { success: false, error: { kind: "session_expired", code: 20000, message: "No active session" } };
  }

  const first = await performRequest(opts);
  if (first.error?.kind !== "session_expired") return first;

  // An unauthed endpoint (login, check-username) answering 401 says nothing
  // about a session — there isn't one to invalidate or refresh.
  if (!auth) return first;

  if (noRefresh) {
    // The refresh call itself. Its caller (doRefresh) owns the verdict.
    return first;
  }

  if (keepalive) {
    // Unload flush (Brief 194 gotcha "page unload mid-refresh"): there is no
    // budget for a second round-trip while the page is being torn down, and
    // the row is durable in the outbox either way.
    notifySessionInvalid("expired");
    return first;
  }

  if (tokenAtSend === DEV_BYPASS_TOKEN) {
    // ?dev=1 mock session: no refresh, and the pre-194 behaviour (a 401 ends
    // the mock session) is preserved exactly.
    notifySessionInvalid("expired");
    return first;
  }

  // Another caller's refresh landed while this request was in flight, so this
  // 401 is stale. Retry with the CURRENT token — firing a refresh here would
  // renew a token that has already been renewed, and JWT
  // rotation makes that fatal.
  const tokenNow = getSession()?.token;
  if (tokenNow && tokenNow !== tokenAtSend) {
    return retryOnce(opts);
  }

  const outcome = await refreshSession();
  if (outcome !== "ok") {
    // "displaced" already notified with its own, non-deferrable reason.
    if (outcome !== "displaced") notifySessionInvalid("expired");
    return first;
  }

  return retryOnce(opts);
}

/**
 * The one retry. A second 401 here is terminal — we do NOT refresh again.
 * @param {RequestOpts} opts
 * @returns {Promise<CrmResult>}
 */
async function retryOnce(opts) {
  const retried = await performRequest(opts);
  if (retried.error?.kind === "session_expired") notifySessionInvalid("expired");
  return retried;
}

// ---------------------------------------------------------------------------
// 1. Auth
// ---------------------------------------------------------------------------

/**
 * Normalise the web-login payload's field casing (CRM uses snake_case, e.g.
 * `expires_in`, `license_end_date`) into the camelCase shape the rest of the
 * app consumes. Tolerant of either casing since the exact wire shape is
 * unconfirmed until the live spec is read (Brief 188 §"Pre-flight status").
 * @param {any} raw
 * @returns {any}
 */
function normaliseLoginData(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const user = raw.user;
  const session = raw.session;
  const device = raw.device;
  return {
    token: raw.token ?? raw.access_token,
    tokenType: raw.token_type ?? raw.tokenType,
    expiresIn: raw.expires_in ?? raw.expiresIn,
    // No refresh token is read here. Allen (2026-11-01): there is no refresh
    // token on the client at all — the server keeps it against the session —
    // so login stores exactly what it stored before Brief 194.
    user: user ? {
      id: user.id,
      username: user.username,
      name: user.name,
      licenseNum: user.license_num ?? user.licenseNum,
      licenseEndDate: user.license_end_date ?? user.licenseEndDate,
    } : undefined,
    session: session ? {
      id: session.id,
      clientType: session.client_type ?? session.clientType,
    } : undefined,
    device: device ? {
      customerDeviceId: device.customer_device_id ?? device.customerDeviceId,
      uaid: device.uaid,
      deviceModel: device.device_model ?? device.deviceModel,
    } : undefined,
  };
}

/**
 * The primary login path (replaces Brief 148's crmActivate-for-login
 * assumption). On success the CALLER (kfe-login.js) writes the session via
 * auth-store.setSession(data) — this function returns the normalised payload
 * but does not persist it.
 *
 * Body is exactly {username, password} — Allen confirmed 2026-08-12 the server
 * takes only these two fields (no language_code, no client_type). Form-encoded
 * works (Laravel accepts both form and JSON for this endpoint); form kept for
 * consistency with the rest of the auth surface.
 * @param {{ username: string, password: string }} params
 * @returns {Promise<CrmResult>}
 */
export async function crmWebLogin({ username, password }) {
  const result = await request({
    method: "POST",
    path: "/app/auth/web-login",
    isLogin: true,
    form: { username, password: preparePassword(password) },
  });
  if (!result.success) return result;
  return { success: true, data: normaliseLoginData(result.data) };
}

/**
 * Invalidate the server-side session. Does not clear the local auth-store
 * session — the caller does that (mirrors crmWebLogin not writing it).
 * @returns {Promise<CrmResult>}
 */
export async function crmLogout() {
  return request({ method: "DELETE", path: "/app/auth/logout", auth: true });
}

// ---------------------------------------------------------------------------
// 2. Forgot-password
// ---------------------------------------------------------------------------

/**
 * @param {{ username: string }} params
 * @returns {Promise<CrmResult>}
 */
export async function crmCheckUsername({ username }) {
  return request({ method: "GET", path: "/app/check-username", query: { username } });
}

/**
 * @param {{ username: string, sendVia: 1|2|3 }} params 1=SMS, 2=email, 3=both
 * @returns {Promise<CrmResult>}
 */
export async function crmGenerateOtp({ username, sendVia }) {
  return request({ method: "POST", path: "/app/generate-otp", form: { username, send_via: sendVia } });
}

// ---------------------------------------------------------------------------
// 3. Trial / entitlement
// ---------------------------------------------------------------------------

/**
 * @param {{ login: string }} params username or email
 * @returns {Promise<CrmResult>}
 */
export async function crmCheckMember({ login }) {
  return request({ method: "GET", path: "/app/check-member", auth: true, query: { login } });
}

/** @returns {Promise<CrmResult>} */
export async function crmSubscriptionStatus() {
  return request({ method: "GET", path: "/app/subscription/status", auth: true });
}

/**
 * NOT WIRED. The staging snapshot has no `/app/activate-trial` — only
 * `/app/subscribe` + `/app/check-member` exist, and neither is confirmed as
 * the "Start free trial" CTA's target. Do not guess it onto `/app/subscribe`
 * (Brief 188 §3). Throws so an accidental call fails loud instead of silently
 * hitting the wrong endpoint. TODO: confirm the trial-signup endpoint with
 * the CRM owner, then implement for real.
 * @returns {never}
 */
export function crmStartTrial() {
  throw new Error("crmStartTrial: not wired — trial-signup endpoint unconfirmed (Brief 188 §3).");
}

// ---------------------------------------------------------------------------
// 4. Session-conflict / device-switch transport (UI is a separate brief)
// ---------------------------------------------------------------------------

/**
 * Also doubles as the session heartbeat.
 * @param {{ sessionResolution?: "wait"|"accept_loss"|"reconcile" }} [params]
 * @returns {Promise<CrmResult>}
 */
export async function crmTouchBase({ sessionResolution } = {}) {
  return request({
    method: "POST",
    path: "/app/touch-base",
    auth: true,
    json: sessionResolution ? { session_resolution: sessionResolution } : {},
  });
}

/**
 * Last-gasp: convert a pending reconciliation to accept_loss. Returns
 * `converted:0` in `data` when nothing was pending.
 * @returns {Promise<CrmResult>}
 */
export async function crmAcceptLoss() {
  return request({ method: "POST", path: "/app/session/accept-loss", auth: true, json: {} });
}

// ---------------------------------------------------------------------------
// 5. Sync transport — signatures only. Wiring (outbox/inbox/reducer) is the
//    sync-engine brief's job (Brief 025's successor); these are thin
//    request() wrappers so that brief has a stable contract to build against.
// ---------------------------------------------------------------------------

/**
 * @param {Record<string, unknown>} progress
 * @param {{ keepalive?: boolean }} [opts] set on the unload flush path (Brief 025)
 * @returns {Promise<CrmResult>}
 */
export async function crmAddLearnerProgress(progress, { keepalive = false } = {}) {
  return request({ method: "POST", path: "/app/add-learner-progress", auth: true, json: progress, keepalive });
}

/**
 * @param {Record<string, unknown>} progress
 * @param {{ keepalive?: boolean }} [opts] set on the unload flush path (Brief 025)
 * @returns {Promise<CrmResult>}
 */
export async function crmUpdateLearnerProgress(progress, { keepalive = false } = {}) {
  return request({ method: "POST", path: "/app/update-learner-progress", auth: true, json: progress, keepalive });
}

/**
 * `download_type` is required by the server (2026-09-14 live smoke: 400
 * "The download type field is required" without it). iOS canon at
 * `KFESyncUserDataMgr.m:143-160`:
 *   • 1 = fresh install OR "Download all my progress data since ever" ON
 *         → server returns FULL history
 *   • 0 = normal path with a valid last-sync cursor
 *         → server returns delta since that cursor
 * Web mirrors this: no local cursor yet → send 1 (full pull); have a cursor
 * from a prior successful pull → send 0 (delta).
 * @param {{ downloadType?: 0 | 1 }} [params]
 * @returns {Promise<CrmResult>}
 */
export async function crmQueryLearnerProgress({ downloadType = 0 } = {}) {
  return request({
    method: "GET",
    path: "/app/query-learner-progress",
    auth: true,
    query: { download_type: downloadType },
  });
}

/**
 * `download_type` mirrors `crmQueryLearnerProgress` (iOS uses the same param
 * dict for both calls — `KFESyncUserDataMgr.m:143-160`). Defaults to 0 (delta);
 * caller passes 1 when there's no local cursor / fresh install.
 * @param {{ since?: string|number, downloadType?: 0 | 1 }} [params]
 * @returns {Promise<CrmResult>}
 */
export async function crmDownloadLearnerProgress({ since, downloadType = 0 } = {}) {
  const query = { download_type: downloadType };
  if (since != null) query.since = since;
  return request({ method: "GET", path: "/app/download-learner-progress", auth: true, query });
}

/**
 * @param {{ since: string|number }} params
 * @returns {Promise<CrmResult>}
 */
export async function crmUpdateLastSync({ since }) {
  return request({ method: "PATCH", path: "/app/update-last-sync", auth: true, json: { since } });
}

/**
 * @param {{ since?: string|number }} [params]
 * @returns {Promise<CrmResult>}
 */
export async function crmContentUpdate({ since } = {}) {
  return request({ method: "POST", path: "/app/content-update", auth: true, json: { since: since ?? 0 } });
}
